import React from 'react';
import {
  MapPin, Calendar, Clock, DollarSign, Briefcase, FileText, Database, Gift, Activity, Award, Building2
} from 'lucide-react';
import { TooltipContext } from './TooltipContext';
import TimedPill from './TimedPill';
import PillList from './PillList';
import { SHIFT_STRUCTURES } from '../../utils/scheduleVocabulary';
import InfoRow from './InfoRow';
import ListingSourceBadge from './ListingSourceBadge';
import TrustBadgeRow, { listingLevelBadges } from '../shared/TrustBadgeRow';
import LicenseMatchBadges from '../shared/LicenseMatchBadges';
import { licenseRequirementPills } from '../Employers/card/listingLicenseRequirements';
import CardEditRow from './CardEditRow';
import { formatCaseType } from '../../utils/listingTransformUtils';
import MarketBandTooltip from './MarketBandTooltip';
import { getCoverageShift, coverageShiftLabels } from '../../utils/coverageShift';

const dash = '–';

// Format facility type string to proper case (e.g., "primary_care" -> "Primary Care")
const formatFacilityTypeString = (facilityType) => {
  if (!facilityType) return dash;

  // Replace underscores with spaces and capitalize each word
  return facilityType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Map backend values to user-friendly display labels
const facilityTypeDisplayMap = {
  'hospital': 'Hospital',
  'ambulatory_surgery_center': 'Ambulatory Surgery Center',
  'office_based_practice': 'Office-Based Practice',
  'pain_management_clinic': 'Pain Management Clinic',
  'emergency_department': 'Emergency Department',
  'obstetrics_birthing': 'Obstetrics / Birthing',
  'other': 'Other'
};

// Get user-friendly display name for a facility type
const getFacilityTypeDisplayName = (type) => {
  if (!type || type === dash) return dash;

  // If it's already properly formatted, return it
  if (type.includes(' ') && type.charAt(0) === type.charAt(0).toUpperCase()) {
    return type;
  }

  // Try to use the lookup map first
  const lowercaseType = type.toLowerCase();
  if (facilityTypeDisplayMap[lowercaseType]) {
    return facilityTypeDisplayMap[lowercaseType];
  }

  // Otherwise, format using the standard formatter
  return formatFacilityTypeString(type);
};

// onEditRow(key) is the listing card-builder seam, the exact counterpart of the
// one ProfileFront exposes for the anonymous /providers card. Present only on
// the employers builder (/employers/create step 2), where the card a provider
// will see IS the form: every row becomes tappable and routes its key
// (facility, pay, location, dates, assignment, schedule, bonus, certifications)
// to the host's fact sheet. Absent -- the provider deck, the swipe surfaces,
// admin previews, /s/ shares -- rendering is byte-for-byte unchanged.
//
// hideEmptyRows is the listing counterpart of the prop CRNAProfileCard already
// threads into ProfileFront/ProfileBack ("sections they never filled in are
// omitted, not rendered as dashes"). With it on, a row with nothing in it
// renders NOTHING: no dash pill, no lone icon, no empty container still holding
// vertical space. The per-row `empty` flags below are the input, so what counts
// as empty here is exactly what builder mode already treats as empty. Default
// FALSE, so the ~30 surfaces that do not opt in are untouched.
const ListingFront = ({ profile = {}, isCertified = false, showClinicalInfo = false, sourceType = 'internal', alignmentData = null, viewerType = 'provider', onEditRow = null, hideSourceBadge = false, hideEmptyRows = false }) => {
  const [openId, setOpenId] = React.useState(null);
  const builder = typeof onEditRow === 'function';
  // In builder mode a row with nothing in it must still be reachable, so the
  // "is this empty?" question is asked here rather than left to a dash the
  // employer cannot tap. Outside builder mode none of this is consulted.
  const edit = (key) => (builder ? () => onEditRow(key) : null);
  const coverageShift = coverageShiftLabels(getCoverageShift(profile));
  // D1.11.2: card faces carry organization- and requisition-level awards only.
  const trustBadges = listingLevelBadges(profile.trustBadges);
  // D1.11 / recon 3.2 B3: when the organization award is on screen, the source
  // badge stops saying "Verified" so one card cannot show two pills reading
  // the same word about two different things.
  const orgVerified = trustBadges.some((b) => b.key === 'verified_employer');

  const facility = profile.facility || {};
  const { facilityType = dash, location } = facility;
  const locationLabel = location ? location.split(',')[0] : dash;
  const rawFacilityType = profile.facilityType || facilityType;
  // Apply proper formatting to facility type display
  const displayFacilityType = getFacilityTypeDisplayName(rawFacilityType);
  const displayLocation = profile.location || locationLabel;

  // Build compensation display - check for blind, tiered, then new format, then legacy
  // Returns an array of labels so each can be rendered in its own pill/bubble
  let rateLabels = [];
  const compensation = profile.compensation || {};

  // Any listing whose rate is not shown gets the SAME unified "Competitive Rate"
  // badge: internal blind (a group hides a real rate) OR comp-blind (no rate was
  // ever posted). The backend attaches a `marketBand` to either cohort when it
  // resolves one, which drives a tap-to-reveal honest market range. When there is
  // no band (e.g. a blind listing whose segment has too few comparables) the pill
  // still shows "Competitive Rate" with no tooltip (prior behavior preserved).
  const rateNotShown = !!(compensation.isBlind || compensation.isCompBlind || profile.marketBand);
  const marketBandTip = profile.marketBand ? (
    <MarketBandTooltip
      providerType={profile.providerType || profile.section}
      state={profile.state}
      canonicalCategory={profile.assignment?.canonicalCategory}
      band={profile.marketBand}
    />
  ) : null;

  if (rateNotShown) {
    rateLabels = ['Competitive Rate'];
  }
  // Tiered listings: show range across all tiers (hourly and/or annual)
  else if (compensation.tiers?.length > 0) {
    const hourlyMins = compensation.tiers.map(t => t.hourlyMin).filter(Boolean);
    const hourlyMaxes = compensation.tiers.map(t => t.hourlyMax).filter(Boolean);
    if (hourlyMins.length > 0 && hourlyMaxes.length > 0) {
      rateLabels.push(`$${Math.min(...hourlyMins)}–$${Math.max(...hourlyMaxes)}/hr`);
    }
    const annualMins = compensation.tiers.map(t => t.annualMin).filter(Boolean);
    const annualMaxes = compensation.tiers.map(t => t.annualMax).filter(Boolean);
    if (annualMins.length > 0 && annualMaxes.length > 0) {
      rateLabels.push(`$${Math.round(Math.min(...annualMins)/1000)}K–$${Math.round(Math.max(...annualMaxes)/1000)}K/yr`);
    }
  }

  // Non-blind, non-tiered: build from flat fields
  if (rateLabels.length === 0) {
    // New format: hourlyAmount, weeklyAmount, and annualAmount
    if (compensation.hourlyAmount && compensation.hourlyAmount > 0 && compensation.hourlyAmount < 1000) {
      rateLabels.push(`$${compensation.hourlyAmount}/hr`);
    }
    if (compensation.weeklyAmount && compensation.weeklyAmount > 0 && compensation.weeklyAmount < 100000) {
      const formattedWeekly = compensation.weeklyAmount.toLocaleString();
      rateLabels.push(`$${formattedWeekly}/wk`);
    }
    if (compensation.annualAmount && compensation.annualAmount >= 30000 && compensation.annualAmount <= 1000000) {
      const annualK = Math.round(compensation.annualAmount / 1000);
      rateLabels.push(`$${annualK}K/yr`);
    }

    // Legacy format fallbacks
    if (rateLabels.length === 0) {
      if (profile.payAmount) {
        if (profile.payPeriod === 'annual') {
          rateLabels.push(`$${Math.round(profile.payAmount / 1000)}K/yr`);
        } else {
          rateLabels.push(`$${profile.payAmount}/hr`);
        }
      } else if (compensation.amount) {
        if (compensation.period === 'annual') {
          rateLabels.push(`$${Math.round(compensation.amount / 1000)}K/yr`);
        } else {
          rateLabels.push(`$${compensation.amount}/hr`);
        }
      } else {
        rateLabels.push(compensation.rateRange ?? dash);
      }
    }
  }
  // Contract type lookup order: compensation.contractType > contractType > compensation.w2Or1099 > dash
  const contractType = profile.compensation?.contractType || profile.contractType || (profile.compensation?.w2Or1099 ?? dash);

  // Ensure assignment type is always an array
  const assignmentType = Array.isArray(profile.assignmentType) ? profile.assignmentType :
    (profile.assignment?.types ? profile.assignment.types :
      (profile.assignment?.type ? [profile.assignment.type] : []));

  // Format start date - if within 3 weeks, show as "Immediate Start"
  let startDateValue = profile.startDate || (profile.coverageNeeds?.startDate ?? dash);
  if (startDateValue !== dash && String(startDateValue).trim().toLowerCase() === 'immediate') {
    // Listings with no concrete date carry the literal "Immediate"; show it the
    // same way real near-term dates are labeled instead of the bare word.
    startDateValue = 'Immediate Start';
  } else if (startDateValue !== dash) {
    try {
      const startDateObj = new Date(startDateValue);
      const today = new Date();
      const threeWeeksFromNow = new Date();
      threeWeeksFromNow.setDate(today.getDate() + 21);

      if (startDateObj <= threeWeeksFromNow && !profile.schedule?.shiftTimes) {
        startDateValue = "Immediate Start";
      }
    } catch (e) {
      // If date parsing fails, just use the original value
      console.log("Error parsing date:", e);
    }
  }

  if (profile.schedule?.shiftTimes && profile.endDate && profile.endDate !== profile.startDate) {
    startDateValue = `${profile.startDate} to ${profile.endDate}`;
  }
  if (coverageShift) startDateValue = coverageShift.dates;

  // Schedule type with multiple fallbacks (flat -> nested -> coverageNeeds)
  // Always normalize to an array for individual pill rendering
  const rawScheduleType = profile.scheduleType ||
    (profile.schedule?.pattern) ||
    (profile.coverageNeeds?.scheduleType ?? null);

  /*
   * THE SHIFT SHAPE IS A SECOND PILL, NOT A SECOND ROW.
   *
   * The schedule question has two halves -- which DAYS ("Weekdays") and the
   * SHAPE of the week ("3x12") -- and they used to share `pattern`, so the card
   * happened to render whichever one the listing stored. Split apart, reading
   * `pattern` alone would drop the shape off the card entirely for the 2,921 of
   * 2,978 listings that state it and nothing else, and the row would go on to
   * read "Add the schedule" over a listing that had answered.
   *
   * Labelled in the words a recruiter uses ("Three 12s") rather than as the
   * stored code, matching the chips the schedule sheet offers.
   */
  const shiftStructure = profile.shiftStructure || profile.schedule?.shiftStructure || '';
  const dayTypes = Array.isArray(rawScheduleType)
    ? rawScheduleType.filter(Boolean)
    : (rawScheduleType ? rawScheduleType.split(',').map(s => s.trim()).filter(Boolean) : []);
  const shapeLabel = shiftStructure
    ? (SHIFT_STRUCTURES.find(s => s.value === shiftStructure) || {}).label || shiftStructure
    : '';
  // Work-schedule pills use the existing day/shift-structure vocabulary.
  // A dated opening's clock and zone are separate facts, not schedule types.
  const scheduleTerms = [...dayTypes, ...(shapeLabel ? [shapeLabel] : [])];
  const scheduleTypes = scheduleTerms.length ? scheduleTerms : [dash];

  // Schedule boolean properties with multiple fallbacks (nested -> flat)
  const onCall = profile.schedule?.onCall || profile.onCall || false;
  const weekends = profile.schedule?.weekends || profile.weekends || false;

  const estimatedWeeklyHours = profile.estimatedWeeklyHours || (profile.coverageNeeds?.estimatedWeeklyHours ?? dash);

  // Sign-on bonus information
  const hasSignOnBonus = profile.signOnBonus?.offered || profile.compensation?.signOnBonusOffered || false;
  const signOnBonusAmount = profile.signOnBonus?.amount || profile.compensation?.signOnBonusAmount || 0;
  const signOnBonusCommitment = profile.signOnBonus?.commitment || profile.compensation?.signOnBonusCommitmentYears || 0;

  const signOnBonusDisplay = hasSignOnBonus
    ? `$${signOnBonusAmount.toLocaleString()}${signOnBonusCommitment ? ` (${signOnBonusCommitment < 1 ? `${Math.round(signOnBonusCommitment * 12)}mo` : `${signOnBonusCommitment}yr`})` : ''}`
    : "No Bonus";

  // Get clinical info for when we need to show it on the front
  const requiredCaseTypes = (profile.clinicalExpectations?.requiredCaseTypes?.length
    ? profile.clinicalExpectations.requiredCaseTypes.map(formatCaseType)
    : profile.clinical?.requiredCaseTypes?.length
      ? profile.clinical.requiredCaseTypes.map(formatCaseType)
      : [dash]);

  // Check multiple paths for EMR system with fallbacks
  const emrSystem = profile.clinicalExpectations?.emrSystem ||
    profile.facilityDetails?.clinicalExpectations?.emrSystem ||
    profile.clinical?.emrSystem ||
    profile.facility?.clinicalExpectations?.emrSystem ||
    dash;

  // Both clinical fallbacks above are a dash, which is the placeholder
  // hideEmptyRows exists to remove. Asked here, next to the data.
  const hasCaseTypes = requiredCaseTypes.length > 0 && requiredCaseTypes[0] !== dash;
  const hasEmrSystem = Boolean(emrSystem) && emrSystem !== dash;

  // Get required skills
  const skillsRequired = profile.clinical?.skillsRequired || profile.clinicalExpectations?.skillsRequired || {};
  const hasSkills = skillsRequired.ultrasoundBlocks || skillsRequired.epidurals || skillsRequired.spinals || skillsRequired.centralLines;

  // Build skills display array
  const skillsArray = [];
  if (skillsRequired.ultrasoundBlocks) skillsArray.push('Blocks');
  if (skillsRequired.epidurals) skillsArray.push('Epidurals');
  if (skillsRequired.spinals) skillsArray.push('Spinals');
  if (skillsRequired.centralLines) skillsArray.push('Central Lines');

  // Required certifications from clinical data
  const requiredCertifications = profile.clinical?.requiredCertifications?.length
    ? profile.clinical.requiredCertifications
    : [];
  const requiredCredentials = [
    ...(profile.clinical?.stateLicenseRequired === true ? ['State license required'] : []),
    ...requiredCertifications,
    ...licenseRequirementPills(profile),
  ];
  const hasCertifications = requiredCredentials.length > 0;

  // Define reusable field components for ordering
  // 'Location TBD' is listingTransformUtils' placeholder for a listing with
  // no city, so it is an empty row wearing a label, not a value.
  const locationIsEmpty = !displayLocation || displayLocation === dash || displayLocation === 'Location TBD';
  const LocationRow = hideEmptyRows && locationIsEmpty ? null : (
    <CardEditRow
      onEdit={edit('location')}
      label="location"
      Icon={MapPin}
      empty={locationIsEmpty}
      prompt="Add the city"
    >
      <InfoRow Icon={MapPin} alignmentStatus={alignmentData?.location}>
        <PillList
          items={[displayLocation]}
          getTooltip={() => 'Facility location'}
          idPrefix="location"
          alignmentStatus={alignmentData?.location}
        />
      </InfoRow>
    </CardEditRow>
  );

  // Only attach the contract-type pill when there's an actual rate to show, so an
  // unpriced listing renders "$ –" instead of a lone pill that reads like the pay.
  // "Competitive Rate" is not a real rate, so it never gets the contract pill.
  const hasRealRate = rateLabels.length > 0 && rateLabels[0] !== dash && !rateNotShown;

  // "Nothing here yet" for pay means no rate AND no deliberate hide. A blind or
  // comp-blind listing has answered the question ("Competitive Rate"), so it is
  // a filled row that opens the same sheet, not an empty prompt.
  const payIsEmpty = !hasRealRate && !rateNotShown;

  const PayRow = hideEmptyRows && payIsEmpty ? null : (
    <CardEditRow
      onEdit={edit('pay')}
      label="pay"
      Icon={DollarSign}
      empty={payIsEmpty}
      prompt="Add the rate"
    >
    <InfoRow Icon={DollarSign} alignmentStatus={alignmentData?.compensation}>
      <div className="pay-contract-details">
        {rateLabels.map((label, i) => (
          <TimedPill
            key={`pay-rate-${i}`}
            id={`pay-rate-${i}`}
            label={label}
            // Comp-blind / blind pill: the tap-to-reveal band (when the backend
            // resolved one) frames the market range, never this listing's offer.
            // No band -> no tooltip, preserving prior "Competitive Rate" behavior.
            tooltipText={
              rateNotShown
                ? (marketBandTip || undefined)
                : (label.includes('/hr') ? 'Hourly compensation range' : label.includes('/yr') ? 'Annual salary range' : 'Compensation range for this position')
            }
            tooltipClassName={rateNotShown ? 'market-band-tip' : undefined}
            className={rateNotShown ? 'market-band-pill' : ''}
            alignmentStatus={alignmentData?.compensation}
          />
        ))}
        {/* Show contract type inline for provider view — only when a real rate is
            present, otherwise the lone pill reads as the pay (e.g. "$ – 1099"). */}
        {(viewerType === 'provider' || !!profile.schedule?.shiftTimes) && hasRealRate && contractType !== dash && (
          <PillList
            items={[contractType]}
            getTooltip={() => 'W2 = employed; 1099 = contract'}
            idPrefix="contract"
            alignmentStatus={alignmentData?.contractType}
          />
        )}
      </div>
    </InfoRow>
    </CardEditRow>
  );

  const startDateIsEmpty = !startDateValue || startDateValue === dash;
  const StartDateRow = hideEmptyRows && startDateIsEmpty ? null : (
    <CardEditRow
      onEdit={edit('dates')}
      label="start date"
      Icon={Calendar}
      empty={startDateIsEmpty}
      prompt="Add the start date"
    >
      <InfoRow Icon={Calendar} alignmentStatus={alignmentData?.startDate}>
        <TimedPill
          id="start-date"
          label={startDateValue}
          tooltipText="Start date for this assignment"
          alignmentStatus={alignmentData?.startDate}
        />
      </InfoRow>
    </CardEditRow>
  );

  const DurationRow = hideEmptyRows && !assignmentType.length ? null : (
    <CardEditRow
      onEdit={edit('assignment')}
      label="assignment type"
      Icon={Briefcase}
      empty={!assignmentType.length}
      prompt="Add the assignment type"
    >
      <InfoRow Icon={Briefcase} alignmentStatus={alignmentData?.workType}>
        <PillList
          items={assignmentType}
          getTooltip={type => `Assignment type: ${type}`}
          idPrefix="assignment"
          alignmentStatus={alignmentData?.workType}
        />
      </InfoRow>
    </CardEditRow>
  );

  // The schedule row carries three facts (pattern, hours, the on-call/weekend
  // flags) and hours always has a value, so "empty" here means the pattern the
  // employer actually chooses is still unset.
  const scheduleIsEmpty = !scheduleTypes.length || scheduleTypes[0] === dash;
  // ...and "always has a value" means it falls back to a DASH, which is the one
  // thing hideEmptyRows must not draw. This row is the visible half of the bug:
  // an unposted pattern rendered PillList's empty dash right next to the hours,
  // so the row read "— 40". Each of the three facts is therefore asked about on
  // its own, and the row survives only if one of them answers.
  const hasWeeklyHours = Boolean(estimatedWeeklyHours) && estimatedWeeklyHours !== dash;
  const scheduleItems = hideEmptyRows && scheduleIsEmpty ? [] : scheduleTypes;
  const scheduleRowIsBare = scheduleIsEmpty && !hasWeeklyHours && !onCall && !weekends;

  const ScheduleRow = hideEmptyRows && scheduleRowIsBare ? null : (
    <CardEditRow
      onEdit={edit('schedule')}
      label="schedule"
      Icon={Clock}
      empty={scheduleIsEmpty}
      prompt="Add the schedule"
    >
      {/* The row takes the schedule verdict as a whole; the pills take it per
          term, because "Weekdays, Call" is two facts and only one of them may
          be what the provider asked for. */}
      <InfoRow Icon={Clock} alignmentStatus={alignmentData?.schedule?.overall}>
        <div className="schedule-details">
          <PillList
            items={scheduleItems}
            getTooltip={type => `Schedule type: ${type}`}
            idPrefix="schedule"
            alignmentStatus={alignmentData?.schedule}
            hideWhenEmpty={hideEmptyRows}
          />
          {(hasWeeklyHours || (!hideEmptyRows && !profile.schedule?.shiftTimes)) && (
            <TimedPill
              id="estimated-weekly-hours"
              label={estimatedWeeklyHours}
              tooltipText="Estimated weekly hours for this assignment"
            />
          )}
          {onCall && (
            <TimedPill
              id="on-call-required"
              label="On-Call"
              tooltipText="On-call is required for this position"
            />
          )}
          {weekends && (
            <TimedPill
              id="weekends-required"
              label="Weekends"
              tooltipText="Weekend shifts are required for this position"
            />
          )}
        </div>
      </InfoRow>
    </CardEditRow>
  );

  // Render fields in role-based order
  // Group users: Start Date → Location → Pay → Duration → Schedule
  // Provider users: Pay → Location → Start Date → Duration → Schedule
  //
  // The two dividers are the same two ProfileFront draws, in the same two
  // places (before the work-type row and before the schedule row). A listing
  // card and a provider card are read side by side wherever a match is being
  // judged, and without these the listing's rows sat tighter than the
  // provider's, so equivalent facts did not line up across the pair.
  const renderOrderedFields = () => {
    // hideEmptyRows can null any of these out, and a divider with nothing on one
    // side of it is exactly the leftover the flag exists to remove. With the
    // flag off every row above is non-null, so both dividers draw as before.
    const hasLeadingRows = Boolean(PayRow || LocationRow || StartDateRow);
    const showDurationDivider = Boolean(DurationRow) && hasLeadingRows;
    const showScheduleDivider = Boolean(ScheduleRow) && (hasLeadingRows || Boolean(DurationRow));

    if (viewerType === 'group') {
      return (
        <>
          {StartDateRow}
          {LocationRow}
          {PayRow}
          {showDurationDivider && <hr className="hand-drawn-divider" />}
          {DurationRow}
          {showScheduleDivider && <hr className="hand-drawn-divider" />}
          {ScheduleRow}
        </>
      );
    }
    // Provider (default) view
    return (
      <>
        {PayRow}
        {LocationRow}
        {StartDateRow}
        {showDurationDivider && <hr className="hand-drawn-divider" />}
        {DurationRow}
        {showScheduleDivider && <hr className="hand-drawn-divider" />}
        {ScheduleRow}
      </>
    );
  };

  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      <div className="card-face-content listing-gradient-bg" role="region" aria-label="Listing Front">
        <div className="card-header">
          {/* Builder mode: the facility type IS the card's headline, so it is
              edited on the card like every other fact rather than in a control
              bolted above it. Mirrors ProfileFront's credential title. */}
          <CardEditRow
            onEdit={edit('facility')}
            label="facility type"
            className="crna-cardedit--title"
          >
          <div className="card-header-top">
            <h2
              className={`card-title${displayFacilityType.length > 18 ? ' card-title-compact' : ''}`}
              style={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                maxWidth: '100%',
                lineHeight: '1.3',
                fontWeight: 700,
                margin: 0,
                paddingRight: '0.5rem',
                flex: 1
              }}
            >
              {displayFacilityType}
            </h2>
            {/* Opt-in suppression, default off, so every viewer surface is
                byte-for-byte unchanged. It exists because a card being BUILT
                has no source yet: the builder's mapper stamps
                sourceType: 'internal' (listingValueMappers.js toRawListing) and
                this badge renders that word as "Verified", which on an
                unpublished draft inside the signed-in app is a trust claim
                about a listing no human has looked at. The host that knows
                whether the row is reviewed is the one that says so. */}
            {hideSourceBadge ? null : (
              <ListingSourceBadge sourceType={sourceType} orgVerified={orgVerified} />
            )}
            {trustBadges.length > 0 && <TrustBadgeRow badges={trustBadges} />}
            <LicenseMatchBadges relevance={profile.licenseRelevance} />
          </div>
          </CardEditRow>
        </div>

        {/* Provider/public surfaces receive only the server-materialized public
            identity. Never fall back to a legacy facilityName here: old rows
            may contain a private facility name. */}
        {/* IN THE BUILDER TOO, and that is the point of showing both. The
            builder's card is drawn from the provider-facing projection, so
            without this row the recruiter has no way to tell the public name
            from the private facility below it: they saw one row labelled
            "facility" reading the company name and concluded the facility they
            had just added had not saved. Absent everywhere the host does not
            supply a public identity, which is every anonymous host. */}
        {profile.publicDisplayName ? (
          <InfoRow Icon={Building2} testId="card-public-name">
            <PillList
              items={[profile.publicDisplayName]}
              getTooltip={() =>
                builder
                  ? 'What providers see. Set in Company Settings.'
                  : 'The public name chosen by this employer'
              }
              idPrefix="public-listing-name"
            />
          </InfoRow>
        ) : null}

        {/* Builder only: the private facility relationship used for operations.
            A provider sees the separate listing-level public name chosen by the
            company; this facility name appears only when that choice is
            explicitly “Facility name.” */}
        {builder && (
          <CardEditRow
            onEdit={edit('facilityOrg')}
            label="facility"
            Icon={Building2}
            empty={!profile.facilityOrgName}
            prompt="Find your facility"
          >
            <InfoRow Icon={Building2} testId="card-facility-org">
              <PillList
                items={[profile.facilityOrgName || dash]}
                getTooltip={() => 'The facility this opening is for'}
                idPrefix="facility-org"
              />
            </InfoRow>
          </CardEditRow>
        )}

        {renderOrderedFields()}

        {/* Sign-On Bonus - shown when offered. In builder mode the row is
            always present so an employer who has one can find where to say so;
            everywhere else an absent bonus stays absent rather than shouting
            "No Bonus" at a provider. */}
        {(hasSignOnBonus || builder) && (
          <CardEditRow
            onEdit={edit('bonus')}
            label="sign-on bonus"
            Icon={Gift}
            empty={!hasSignOnBonus}
            prompt="Add a sign-on bonus"
          >
            <InfoRow Icon={Gift}>
              <TimedPill
                id="sign-on-bonus"
                label={signOnBonusDisplay}
                tooltipText={`Sign-on bonus: ${signOnBonusDisplay}`}
              />
            </InfoRow>
          </CardEditRow>
        )}

        {/* Required credentials - always visible when set */}
        {(hasCertifications || builder) && (
          <CardEditRow
            onEdit={edit('certifications')}
            label="required credentials"
            Icon={Award}
            empty={!hasCertifications}
            prompt="Add required credentials"
          >
            <InfoRow Icon={Award}>
              <PillList
                items={requiredCredentials}
                getTooltip={(credential) =>
                  credential === 'State license required'
                    ? 'Provider must hold a license in this job’s state'
                    : `Required certification: ${credential}`
                }
                idPrefix="cert"
              />
            </InfoRow>
          </CardEditRow>
        )}

        {showClinicalInfo && (
          <>
            {(!hideEmptyRows || hasCaseTypes) && (
              <InfoRow Icon={FileText}>
                <div className="required-case-types-wrapper">
                  <PillList
                    items={requiredCaseTypes}
                    getTooltip={type => `Required case type: ${type}`}
                    idPrefix="case-type"
                  />
                </div>
              </InfoRow>
            )}

            {hasSkills && (
              <InfoRow Icon={Activity}>
                <PillList
                  items={skillsArray}
                  getTooltip={skill => `Required skill: ${skill}`}
                  idPrefix="skill"
                />
              </InfoRow>
            )}

            {(!hideEmptyRows || hasEmrSystem) && (
              <InfoRow Icon={Database}>
                <PillList
                  items={[emrSystem]}
                  getTooltip={() => 'EMR system used by this facility'}
                  idPrefix="emr"
                />
              </InfoRow>
            )}
          </>
        )}
      </div>
    </TooltipContext.Provider>
  );
};

export default ListingFront;
